var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// lib/index.ts
import * as R from "remeda";

// lib/debug/index.ts
var sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// lib/errors/index.ts
var connectionErrorsMap = {
  ErrorMappingIdPositive: "Failed to add connection: mapping ID issues",
  ErrorMappingIdConflict: "Failed to add connection: mapping ID conflicts",
  ErrorLinkSelfLoop: "Failed to add connection: self-loops found",
  ErrorNotFoundNode: "Failed to add connection: node not found",
  ErrorNotFoundLink: "Failed to add connection: connection not found",
  ErrorNodeForLinkDeleted: "Failed to add connection: nodes for connection deleted",
  ErrorNodeForLinkInsertDeleted: "Failed to add connection: nodes for link insertions deleted",
  ErrorLinkInsertImpossible: "Failed to add connection: link insertion impossible",
  ErrorLinkUnique: "Failed to add connection: unique connection issues found",
  ErrorLinkDuplicate: "Failed to add connection: duplicate connection",
  ErrorLinkRequired: "Failed to add connection: required connection is missing"
};

// lib/formatters/index.ts
var formatters_exports = {};
__export(formatters_exports, {
  getAbbr: () => getAbbr,
  toDate: () => toDate,
  toFileSize: () => toFileSize,
  toRelativeDateNotation: () => toRelativeDateNotation
});

// lib/formatters/abbr.ts
var getAbbr = (str) => {
  if (!str)
    return "";
  return str.split(" ").map((word) => word[0]).join("");
};

// lib/formatters/datetime.ts
import dayjs from "dayjs";
var toDate = (date, options) => {
  if (!date) {
    return "N/A";
  }
  switch (options?.preset) {
    case "short":
      return dayjs(date).format("DD.MM.YYYY");
    case "timestamp":
      return dayjs(date).format("YYYY-MM-DDTHH:mm:ss[Z]");
    case "date_struct":
      return dayjs(date).format("YYYY-MM-DD");
    case "full_date":
      return dayjs(date).format("MMMM DD, YYYY");
    case "full_date_with_time":
      return dayjs(date).format("MMMM DD, YYYY HH:mm");
    default:
      return dayjs(date).format("DD.MM.YYYY");
  }
};
var toRelativeDateNotation = (notation) => {
  const direction = notation.startsWith("-") ? "before" : "after";
  const [, , amount, unit] = notation.match(/^(-)?(\d+)([dwMy])$/) || [];
  return {
    direction,
    unit,
    amount: parseInt(amount, 10)
  };
};

// lib/formatters/filesize.ts
import fsUtil from "filesize";
var fileSize = fsUtil.partial({ base: 2, standard: "jedec" });
var toFileSize = (bytes) => {
  return fileSize(bytes);
};

// lib/types/rules.ts
var RuleType = /* @__PURE__ */ ((RuleType2) => {
  RuleType2["DOCUMENT"] = "DOCUMENT";
  RuleType2["SEGMENT"] = "SEGMENT";
  return RuleType2;
})(RuleType || {});

// lib/utils/array.ts
var array_exports = {};
__export(array_exports, {
  groupBy: () => groupBy,
  partition: () => partition,
  removeDuplicates: () => removeDuplicates
});
var groupBy = (arr, key) => arr.reduce((groups, item) => {
  (groups[key(item)] ||= []).push(item);
  return groups;
}, {});
function partition(array, condition) {
  return array.reduce(
    ([pass, fail], elem) => {
      return condition(elem) ? [[...pass, elem], fail] : [pass, [...fail, elem]];
    },
    [[], []]
  );
}
var objectsAreEqual = (objA, objB) => {
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);
  if (keysA.length !== keysB.length) {
    return false;
  }
  for (const key of keysA) {
    if (typeof objA[key] === "object" && objA[key] !== null && typeof objB[key] === "object" && objB[key] !== null) {
      if (!objectsAreEqual(objA[key], objB[key])) {
        return false;
      }
    } else if (objA[key] !== objB[key]) {
      return false;
    }
  }
  return true;
};
var getUniqueObjects = (arr) => {
  return arr.filter(
    (obj, index) => index === arr.findIndex((otherObj) => objectsAreEqual(obj, otherObj))
  );
};
var removeDuplicates = (arr1, arr2) => {
  const commonObjects = arr1.filter((objA) => arr2.some((objB) => objectsAreEqual(objA, objB)));
  const uniqueCommonObjects = getUniqueObjects(commonObjects);
  const uniqueArr1 = arr1.filter((objA) => !arr2.some((objB) => objectsAreEqual(objA, objB)));
  const uniqueArr2 = arr2.filter((objA) => !arr1.some((objB) => objectsAreEqual(objA, objB)));
  return [...uniqueCommonObjects, ...uniqueArr1, ...uniqueArr2];
};

// lib/utils/countries.ts
var countries_exports = {};
__export(countries_exports, {
  groupCountries: () => groupCountries
});
function groupCountries(data) {
  const hasGrouping = data.some((item) => item.group);
  if (!hasGrouping) {
    return data;
  }
  return data.reduce((acc, { group, value, label }) => {
    if (group) {
      const existingGroup = acc.find((g) => g.group === group);
      const newItem = { value, label };
      if (existingGroup) {
        existingGroup.items.push(newItem);
      } else {
        acc.push({ group, items: [newItem] });
      }
    }
    return acc;
  }, []);
}

// lib/utils/datetime.ts
var datetime_exports = {};
__export(datetime_exports, {
  add: () => add,
  calculateTillNow: () => calculateTillNow,
  getOverdueDaysCount: () => getOverdueDaysCount,
  isSameDate: () => isSameDate,
  jsDateFromString: () => jsDateFromString
});
import dayjs2 from "dayjs";
import duration from "dayjs/plugin/duration";
dayjs2.extend(duration);
var add = (date, value, unit) => {
  return dayjs2(date).add(value, unit).toDate();
};
var getOverdueDaysCount = (date) => {
  const today = dayjs2();
  const nextReviewDate = dayjs2(date);
  if (nextReviewDate.isBefore(today, "day")) {
    return today.diff(nextReviewDate, "day");
  }
  return 0;
};
var isSameDate = (date1, date2) => {
  if (!date1 || !date2)
    return false;
  return dayjs2(date1).isSame(dayjs2(date2), "day");
};
var jsDateFromString = (date) => {
  return dayjs2(date).toDate();
};
var calculateTillNow = (givenDate) => {
  const now = dayjs2();
  const past = dayjs2(givenDate);
  const diff = now.diff(past);
  const diffDuration = dayjs2.duration(diff);
  const years = diffDuration.years();
  const months = diffDuration.months();
  const days = diffDuration.days();
  const hours = diffDuration.hours();
  let result = "";
  if (years > 0) {
    result += `${years} year${years > 1 ? "s" : ""}`;
    if (months > 0) {
      result += ` ${months} month${months > 1 ? "s" : ""}`;
    }
  } else if (months > 0) {
    result += `${months} month${months > 1 ? "s" : ""}`;
  } else if (days > 0) {
    result += `${days} day${days > 1 ? "s" : ""}`;
  } else if (hours > 0) {
    result += `${hours} hour${hours > 1 ? "s" : ""}`;
  }
  return result || "0 hours";
};

// lib/utils/money.ts
var money_exports = {};
__export(money_exports, {
  format: () => format
});
var format = (amount) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

// lib/utils/object.ts
var object_exports = {};
__export(object_exports, {
  beautify: () => beautify,
  get: () => get,
  prune: () => prune
});
function get(obj, path, defaultValue) {
  const result = path.split(".").reduce((r, p) => {
    if (typeof r === "object") {
      p = p.startsWith("[") ? p.replace(/\D/g, "") : p;
      return r[p];
    }
    return void 0;
  }, obj);
  return result !== void 0 ? result : defaultValue;
}
function prune(obj) {
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    const hasProperties = value && Object.keys(value).length > 0;
    if (value === null) {
      delete obj[key];
    } else if (typeof value !== "string" && hasProperties) {
      prune(value);
    }
  });
  return obj;
}
var beautify = (obj) => {
  for (const [k, v] of Object.entries(obj)) {
    if (Object(v) === v)
      beautify(v);
    else if (typeof v === "string")
      obj[k] = v.trim();
  }
  return obj;
};

// lib/utils/string.ts
var string_exports = {};
__export(string_exports, {
  abbreviate: () => abbreviate,
  fromSnakeToCamel: () => fromSnakeToCamel,
  prettify: () => prettify,
  slugify: () => slugify,
  uglify: () => uglify
});
var slugify = (value) => value.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/[\s_-]+/g, "-").replace(/^-+|-+$/g, "");
var prettify = (value) => {
  return `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`.replaceAll("-", " ").replaceAll("_", " ");
};
var uglify = (value) => {
  return value.replaceAll("-", "_").toUpperCase();
};
var abbreviate = (value) => {
  return value.split(" ").map((word) => word.charAt(0).toUpperCase()).join("").slice(0, 2);
};
var fromSnakeToCamel = (value) => {
  return value.replace(/_./g, (x) => x[1].toUpperCase());
};

// lib/utils/types.ts
var types_exports = {};
__export(types_exports, {
  assertNotNullish: () => assertNotNullish,
  isNotNullish: () => isNotNullish
});
function assertNotNullish(value, message) {
  if (value === void 0 || value === null) {
    throw new Error(message);
  }
}
var isNotNullish = (value) => {
  return value !== void 0 && value !== null;
};

// lib/validators/is-date.ts
import dayjs3 from "dayjs";
var isDate = (value) => {
  return value instanceof Date && dayjs3(value).isValid();
};

// lib/validators/is-valid-email.ts
function isValidEmail(input) {
  const atIndex = input.indexOf("@");
  if (atIndex <= 0 || atIndex === input.length - 1) {
    return false;
  }
  const dotIndex = input.indexOf(".", atIndex + 1);
  return dotIndex > atIndex + 1 && dotIndex < input.length - 1;
}

// lib/validators/raw-revision.ts
var rawRevision = {
  revision: 1,
  createdAt: "2023-01-01T00:00:00Z",
  updatedAt: "2023-01-01T00:00:00Z",
  etag: ""
};

// lib/validators/validate-segment/validate-segment.ts
import { z as z3 } from "zod";

// lib/validators/config-rules.validator.ts
import dayjs4 from "dayjs";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { z } from "zod";
var ConfigRulesValidator = class {
  values;
  ctx;
  rules;
  constructor(opts) {
    this.values = opts.values;
    this.ctx = opts.ctx;
    this.rules = opts.rules;
  }
  getValue(fieldName) {
    return this.values[fieldName];
  }
  getRule(ruleName) {
    return this.rules.find((rule) => rule.rule === ruleName);
  }
  getDateMethod(direction) {
    return direction === "before" ? "subtract" : "add";
  }
  getDateByNotation(ruleValue) {
    const { unit, direction, amount } = toRelativeDateNotation(ruleValue);
    return dayjs4()[this.getDateMethod(direction)](amount, unit);
  }
  setIssue(message, path) {
    this.ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path
    });
  }
  required(fieldName) {
    const rule = this.getRule("required");
    if (!rule)
      return;
    const value = this.getValue(fieldName);
    const isValid = Array.isArray(value) ? value.length : value !== void 0 && value !== null && value !== "";
    if (!isValid) {
      this.setIssue("Required field", [fieldName]);
    }
  }
  minRelativeDate(fieldName) {
    const rule = this.getRule("minRelativeDate");
    if (!rule)
      return;
    const value = this.getValue(fieldName);
    const date = this.getDateByNotation(rule.value);
    if (dayjs4(value).isAfter(date)) {
      this.setIssue("The specified date is less than the minimum", [fieldName]);
    }
  }
  maxRelativeDate(fieldName) {
    const rule = this.getRule("maxRelativeDate");
    if (!rule)
      return;
    const value = this.getValue(fieldName);
    const date = this.getDateByNotation(rule.value);
    if (dayjs4(value).isBefore(date)) {
      this.setIssue("The specified date is greater than the maximum", [fieldName]);
    }
  }
  phone(fieldName) {
    const value = this.getValue(fieldName);
    if (value && !isPossiblePhoneNumber(value)) {
      this.setIssue("Phone number is invalid", [fieldName]);
    }
  }
  email(fieldName) {
    const value = this.getValue(fieldName);
    if (value && !isValidEmail(value)) {
      this.setIssue("The specified email is not valid", [fieldName]);
    }
  }
};

// lib/validators/shards.ts
import { z as z2 } from "zod";
var zodShards = {
  text: z2.string({
    required_error: "Required field"
  }).transform((value) => value.trim()).refine(
    (value) => value.length >= 2,
    () => ({ message: "Required field" })
  ),
  date: z2.string().refine(
    (value) => /\d{4}-\d{2}-\d{2}/.test(value),
    () => ({ message: `Please enter correct date` })
  ),
  document: z2.object({
    files: z2.array(z2.any()),
    is_no_document: z2.boolean(),
    reason: z2.string().optional()
  }),
  email: z2.string({ required_error: "Email is required, e.g. name@example.com" }).email({ message: "Please enter correct email" })
};

// lib/validators/validate-segment/validate-segment.types.ts
var ValidateSegmentErrorType = /* @__PURE__ */ ((ValidateSegmentErrorType2) => {
  ValidateSegmentErrorType2["SEGMENT"] = "segment";
  ValidateSegmentErrorType2["FIELD"] = "field";
  return ValidateSegmentErrorType2;
})(ValidateSegmentErrorType || {});

// lib/validators/validate-segment/validate-segment.ts
function validateObject(rules, fieldDef, visualRules, values, errors) {
  const segmentRulesForValidate = getRulesForSegment(rules, fieldDef);
  const segmentRulesForVisual = getRulesForSegment(visualRules, fieldDef);
  const { errors: structErrors } = validateSegment({
    values: values[fieldDef.name],
    def: fieldDef,
    rules: segmentRulesForValidate,
    visualRules: segmentRulesForVisual
  });
  if (structErrors.length) {
    errors.push(
      ...structErrors.map(({ message, path, type }) => ({
        type,
        message,
        path: [`${fieldDef.name}.${path}`]
      }))
    );
  }
}
function validateArray(values, fieldDef, rules, visualRules, errors) {
  if (isRequired(rules, fieldDef, "SEGMENT" /* SEGMENT */) && !values[fieldDef.name].length) {
    errors.push({
      path: [fieldDef.name],
      message: "Required segment",
      type: "segment" /* SEGMENT */
    });
  }
  values[fieldDef.name].forEach((value, index) => {
    const segmentRulesForValidate = getRulesForArraySegment(rules, fieldDef, index);
    const segmentRulesForVisual = getRulesForArraySegment(visualRules, fieldDef, index);
    const { errors: structErrors } = validateSegment({
      values: value,
      def: fieldDef,
      rules: segmentRulesForValidate,
      visualRules: segmentRulesForVisual
    });
    if (structErrors.length) {
      errors.push(
        ...structErrors.map(({ message, path, type }) => ({
          type,
          message,
          path: [`${fieldDef.name}.${index}.${path}`]
        }))
      );
    }
  });
}
var validateDocument = ({
  def,
  rules,
  values,
  visualRules,
  errors
}) => {
  if (visualRules.find((item) => item.rule === "hide" && item.path === def.name)) {
    return;
  }
  if (isDocumentUploaderComponentVisible({ visualRules, name: def.name }) && isRequired(rules, def, "DOCUMENT" /* DOCUMENT */)) {
    if (def.isMultiple) {
      if (values[def.name].length) {
        values[def.name].forEach((item, index) => {
          if (!(item.files && item.files.length) && !item.reason) {
            errors.push({
              message: messages.required,
              path: [`${def.name}.${index}.reason`]
            });
          }
        });
      }
    } else {
      if (!(values[def.name].files && values[def.name].files.length) && !values[def.name].reason) {
        errors.push({
          message: messages.required,
          path: [`${def.name}.reason`]
        });
      }
    }
  }
  if (def.isMultiple) {
    validateArray(values, def, rules, visualRules, errors);
  } else {
    validateObject(rules, def, visualRules, values, errors);
  }
};
function refineConfigurationRules(segmentShape, rules, def, visualRules) {
  return z3.object(segmentShape).superRefine((values, ctx) => {
    const validator = new ConfigRulesValidator({ values, ctx, rules });
    if (!def.fields)
      return;
    for (const field of Object.values(def.fields)) {
      const fieldRules = rules.filter((rule) => rule.path === field.name);
      const fieldVisualRules = visualRules.filter((rule) => {
        return rule.path === field.name;
      });
      if (!isFieldVisible(fieldVisualRules)) {
        continue;
      }
      if (field.type === "phone") {
        validator.phone(field.name);
      }
      if (field.type === "email") {
        validator.email(field.name);
      }
      if (fieldRules.length === 0) {
        continue;
      }
      validator.required(field.name);
      if (field.type === "date") {
        validator.minRelativeDate(field.name);
        validator.maxRelativeDate(field.name);
      }
    }
  });
}
function isPossibleParsingStruct(fieldDef, values) {
  return fieldDef.kind === "struct" && values[fieldDef.name];
}
function isSegmentFilled(values, fieldDef) {
  return values.hasOwnProperty(fieldDef.name);
}
function isSegmentVisible(visualRules, fieldDef) {
  return !visualRules.find((item) => fieldDef.name === item.path && item.rule === "hide");
}
function isRequired(rules, fieldDef, type = null) {
  return rules.findIndex(
    (item) => fieldDef.name === item.path && item.rule === "required" && (type ? item.type === type : true)
  ) >= 0;
}
function isDocumentUploaderComponentVisible({
  visualRules,
  name
}) {
  return visualRules.findIndex(
    (item) => item.type === "DOCUMENT" /* DOCUMENT */ && item.rule === "hide" && item.path === name
  ) < 0;
}
var validateSegment = ({
  values,
  def,
  rules,
  visualRules
}) => {
  if (!def.fields || !values) {
    return { errors: [], isValid: true };
  }
  const errors = [];
  const segmentShape = {};
  for (const fieldDef of Object.values(def.fields)) {
    if (isSegmentFilled(values, fieldDef) && isSegmentVisible(visualRules, fieldDef)) {
      segmentShape[fieldDef.name] = getBaseRule(fieldDef);
      if (isPossibleParsingStruct(fieldDef, values)) {
        if (fieldDef.fieldKind === "document") {
          validateDocument({ def: fieldDef, rules, visualRules, errors, values });
        } else {
          if (fieldDef.isMultiple) {
            validateArray(values, fieldDef, rules, visualRules, errors);
          } else {
            validateObject(rules, fieldDef, visualRules, values, errors);
          }
        }
      }
    }
  }
  const scheme = refineConfigurationRules(segmentShape, rules, def, visualRules);
  const result = scheme.safeParse(values);
  const isValid = result.success;
  if (!isValid) {
    errors.push(
      ...result.error.issues.map((value) => ({
        message: value.message,
        path: value.path
      }))
    );
  }
  return { isValid: !errors.length, errors };
};
var startsWithNumber = (str) => {
  return /^\d/.test(str);
};
var messages = {
  required: "Required field"
};
var getRulesForSegment = (rules, fieldDef) => {
  return rules.filter(({ path }) => path.startsWith(`${fieldDef.name}.`)).map(({ path, ...rest }) => ({ path: path.replace(`${fieldDef.name}.`, ""), ...rest }));
};
var getRulesForArraySegment = (rules, fieldDef, index) => {
  const segmentRules = getRulesForSegment(rules, fieldDef);
  return [
    ...segmentRules.filter(({ path }) => path.startsWith(`${index}.`)).map(({ path, ...rest }) => ({ path: path.replace(`${index}.`, ""), ...rest })),
    ...segmentRules.filter(({ path }) => !startsWithNumber(path))
  ];
};
var isFieldVisible = (rules) => {
  return !rules.some((rule) => rule.rule === "hide");
};
var getBaseRule = (field) => {
  if (field.type === "struct") {
    const structShape = {};
    for (const fieldDef of Object.values(field)) {
      structShape[fieldDef.name] = getBaseRule(fieldDef);
    }
    if (field.isMultiple) {
      return z3.array(z3.object(structShape));
    }
    return z3.object(structShape);
  }
  if (field.type === "text") {
    return z3.string({
      required_error: messages.required,
      invalid_type_error: messages.required
    }).transform((value) => value.trim()).optional();
  }
  if (field.type === "number") {
    return z3.number().optional();
  }
  if (field.type === "date") {
    return zodShards.date.optional();
  }
  if (field.type === "boolean") {
    return z3.boolean().optional();
  }
  if (field.type === "enum") {
    const enumRule = z3.any();
    return enumRule.optional();
  }
  return z3.any();
};

// lib/generalGraph/objectId.ts
var protocolPart = "tf://";
var splitObjectId = (objectId) => {
  if (!objectId.startsWith(protocolPart)) {
    throw new Error(`Unexpected object ID format: no ${protocolPart} protocol prefix`);
  }
  const [tenantPart, rest] = objectId.split("@");
  const tenantId = tenantPart.replace(protocolPart, "");
  assertNotNullish(rest, "Unexpected object ID format: only tenant ID is provided");
  const restParts = rest.split("/");
  if (restParts.length < 3) {
    throw new Error(
      `Unexpected object ID format: it must consist of cluster ID, schema tag and object name`
    );
  }
  const clusterId = restParts[0];
  const schemaTag = restParts.slice(1, restParts.length - 1).join("/");
  const [objectName, version] = restParts[restParts.length - 1].split("?");
  return {
    tenantId,
    clusterId,
    schemaTag,
    objectName,
    version
  };
};
var createObjectId = (parts) => {
  const objectId = `${protocolPart}${parts.tenantId}@${parts.clusterId}/${parts.schemaTag}/${parts.objectName}`;
  return parts.version ? `${objectId}?${parts.version}` : objectId;
};
var createObjectAliasId = (parts) => {
  return `${protocolPart}${parts.tenantId}@${parts.clusterId}/${parts.alias}`;
};
var getObjectAlias = (objectAliases, tag) => {
  return Object.entries(objectAliases).find(([_aliasValue, aliasTag]) => aliasTag === tag)?.[0];
};
var getObjectDisplayName = (objectAliases) => {
  return getObjectAlias(objectAliases, "@displayName");
};
var getProfileExternalId = (objectAliases) => {
  return getObjectAlias(objectAliases, "@externalId");
};

// lib/generalGraph/relations.ts
var objectRelationValue = (relationKind, objectId) => ({
  _object: objectId,
  _relationKind: relationKind
});
var profileRelationValue = (profileId) => ({
  _object: profileId,
  _relationKind: "profile"
});

// lib/generalGraph/schemaId.ts
var protocolPart2 = "json-ir://";
var createPublicSchemaId = (parts) => {
  return `${protocolPart2}public@${parts.clusterId}/${parts.schemaTag}`;
};
var createTenantSchemaId = (parts) => {
  return `${protocolPart2}${parts.tenantId}@${parts.clusterId}/${parts.schemaTag}`;
};

// lib/saveFile.ts
var saveFile = (data, fileName) => {
  const blob = data instanceof Blob ? data : new Blob([data]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);
};
export {
  array_exports as A,
  countries_exports as C,
  datetime_exports as D,
  money_exports as M,
  object_exports as O,
  R,
  RuleType,
  string_exports as S,
  types_exports as T,
  ValidateSegmentErrorType,
  connectionErrorsMap,
  createObjectAliasId,
  createObjectId,
  createPublicSchemaId,
  createTenantSchemaId,
  formatters_exports as fmt,
  getObjectAlias,
  getObjectDisplayName,
  getProfileExternalId,
  isDate,
  isValidEmail,
  objectRelationValue,
  profileRelationValue,
  rawRevision,
  saveFile,
  sleep,
  splitObjectId,
  validateSegment
};
//# sourceMappingURL=index.mjs.map